/**
 * Pure predicates behind the board filter bar.
 *
 * They live here, and not in TaskBoardTab, for one reason: that component is
 * ~2,200 lines of dnd-kit/presence/collab wiring and cannot be exercised
 * without a DOM. A filter that silently stops matching is invisible — the
 * board just looks empty — so the matching rules have to be testable on their
 * own. Nothing in this file touches the DOM, a store, or `Date.now()` except
 * through an injected `now`.
 *
 * Filter state is UI state. It is never written into a task, never reaches
 * `data`, and never syncs — every function here READS tasks and returns
 * booleans or a new array of the SAME task objects.
 */

import { parseTaskKey } from './taskKeys';
import { subtasksMatch } from './subtasks';
import { matchesLabels } from './boardLabels';

/** The neutral filter set: everything shows. */
export const EMPTY_FILTERS = Object.freeze({
  search: '',
  mine: false,
  overdue: false,
  unassigned: false,
  // Selected label ids. An array rather than a boolean, so it is the one
  // filter that can be partially on — hence the `.length` check everywhere
  // instead of a truthiness test (an empty array is truthy).
  labels: [],
  // One priority level ('high' | 'medium' | 'low') or '' for any (card 4.3).
  // Single-select like Trello's quick filter: the chip is a picker, and "high
  // or low but not medium" is not a question people ask. Not named `priority`:
  // that is a task field, and no filter key may share a name with one (the
  // "filter state never reaches sync" test checks a task's record data for
  // every key here).
  priorityLevel: '',
  // '' | 'done' | 'open' — the toolbar's "N done · N open" pill (owner, 13.10.2026).
  state: '',
});

/** The values the done/open filter takes. */
export const STATE_FILTERS = Object.freeze(['done', 'open']);

/** The levels the priority filter offers, highest first (the menu order). */
export const PRIORITY_FILTER_LEVELS = Object.freeze(['high', 'medium', 'low']);

/** True when at least one filter narrows the board. Drives both the "n of m"
 *  count and the drag lock (see TaskBoardTab). */
export function filtersActive(filters) {
  const f = filters || EMPTY_FILTERS;
  return !!(
    (f.search || '').trim() ||
    f.mine ||
    f.overdue ||
    f.unassigned ||
    (f.labels || []).length ||
    PRIORITY_FILTER_LEVELS.includes(f.priorityLevel) ||
    STATE_FILTERS.includes(f.state)
  );
}

/**
 * How many filters narrow the board — the N of "Clear N" (card 4.1). Each
 * toggle chip, each selected label and the priority pick count one; a
 * non-blank search counts one too, because "Clear" empties the search box as
 * well and a "Clear 0" next to a typed query would be a lie.
 */
export function activeFilterCount(filters) {
  const f = filters || EMPTY_FILTERS;
  return (
    ((f.search || '').trim() ? 1 : 0) +
    (f.mine ? 1 : 0) +
    (f.overdue ? 1 : 0) +
    (f.unassigned ? 1 : 0) +
    (f.labels || []).length +
    (PRIORITY_FILTER_LEVELS.includes(f.priorityLevel) ? 1 : 0) +
    (STATE_FILTERS.includes(f.state) ? 1 : 0)
  );
}

/** Re-exported so callers filtering a board need one import, not two. */
export { matchesLabels };

/**
 * Strip tags from a description without a DOM. Tasks carry `descriptionHtml`
 * (and, on team boards, a Lexical mirror); searching the raw HTML would match
 * on markup — typing "div" or "span" would hit every card with a description.
 */
function plainText(html) {
  return String(html || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ');
}

/** Substring over title + description text + subtask titles. An empty query
 *  matches everything — a blank search box must not empty the board.
 *
 *  With the task's key (`taskKey`, "APB-42"; commit ↔ task links slice 2) a
 *  query that IS a key finds exactly that task — "apb-4" is APB-4, never
 *  APB-42 — also through an old prefix of a renamed board (`keyAliases`);
 *  any other query also matches inside the key ("apb-" lists the board). */
export function matchesSearch(task, query, { taskKey = null, keyAliases = [] } = {}) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return true;
  if (taskKey) {
    const asKey = parseTaskKey(q);
    const own = parseTaskKey(taskKey);
    if (asKey && own) {
      if (asKey.number !== own.number) return false;
      return asKey.prefix === own.prefix || (keyAliases || []).some((p) => String(p).toUpperCase() === asKey.prefix);
    }
    if (taskKey.toLowerCase().includes(q)) return true;
  }
  const haystack = `${task?.title || ''} ${plainText(task?.descriptionHtml)}`.toLowerCase();
  return haystack.includes(q) || subtasksMatch(task, q);
}

/** Assigned to the signed-in user. With no known user id nothing is "mine" —
 *  returning true there would make the filter a no-op instead of an obvious
 *  empty result. */
export function matchesMine(task, currentUserId) {
  if (currentUserId == null) return false;
  return task?.assigneeId != null && String(task.assigneeId) === String(currentUserId);
}

/**
 * Due strictly before today (local midnight). This is the canonical
 * implementation — TaskBoardTab's `rowIsOverdue` and TaskCard both delegate
 * here so the badge on a card and the Overdue filter can never disagree.
 *
 * A `YYYY-MM-DD` due date (what the date picker writes) is a LOCAL calendar
 * day: `new Date('2026-10-12')` is UTC midnight, which west of Greenwich is
 * the previous evening and made a task "overdue" on its own due day.
 */
export function isOverdue(dueDate, now = new Date()) {
  if (!dueDate) return false;
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dueDate));
  const d = ymd ? new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3])) : new Date(dueDate);
  if (Number.isNaN(d.getTime())) return false;
  const today = new Date(now.getTime());
  today.setHours(0, 0, 0, 0);
  return d < today;
}

export function matchesOverdue(task, now) {
  return isOverdue(task?.dueDate, now);
}

/** A task's priority as the board shows it: anything missing or unknown is
 *  medium — every task is created medium, and the card renders it so. */
export function priorityOf(task) {
  return PRIORITY_FILTER_LEVELS.includes(task?.priority) ? task.priority : 'medium';
}

/** '' (or anything that is not a level) is "any priority". */
export function matchesPriority(task, priority) {
  if (!PRIORITY_FILTER_LEVELS.includes(priority)) return true;
  return priorityOf(task) === priority;
}

/** '' is "done and open"; 'done' / 'open' keep only those (task.done is the
 *  authority for closed, as everywhere on the board). */
export function matchesState(task, state) {
  if (state === 'done') return !!task?.done;
  if (state === 'open') return !task?.done;
  return true;
}

/** "What is unowned?" — null, undefined and '' all count as unassigned. */
export function matchesUnassigned(task) {
  return task?.assigneeId == null || task.assigneeId === '';
}

/**
 * All active filters must hold — they INTERSECT, never union. "Mine + Overdue"
 * asks for my overdue cards, not for everything of mine plus everything late.
 */
export function taskMatchesFilters(task, filters, { currentUserId = null, now, keyOf = null, keyAliases = [] } = {}) {
  const f = filters || EMPTY_FILTERS;
  if (!matchesSearch(task, f.search, { taskKey: keyOf ? keyOf(task) : null, keyAliases })) return false;
  if (f.mine && !matchesMine(task, currentUserId)) return false;
  if (f.overdue && !matchesOverdue(task, now)) return false;
  if (f.unassigned && !matchesUnassigned(task)) return false;
  // Labels intersect with the rest but union among themselves — see
  // matchesLabels. Mine + (bug OR regression) is my bugs and my regressions.
  if (!matchesLabels(task, f.labels)) return false;
  if (!matchesPriority(task, f.priorityLevel)) return false;
  if (!matchesState(task, f.state)) return false;
  return true;
}

/** Same task objects, fewer of them — deliberately no mapping/cloning, so a
 *  filter pass can never stamp a UI-only field onto something that syncs. */
export function filterTasks(tasks, filters, ctx) {
  const list = tasks || [];
  if (!filtersActive(filters)) return list;
  return list.filter((t) => taskMatchesFilters(t, filters, ctx));
}

/**
 * The value the assignee picker should show for a task.
 *
 * Extracted from TaskDetailModal so it can be tested at all. It lived inline in
 * a 700-line component, and an adversarial review proved the consequence:
 * replacing it with a constant `UNASSIGNED` — which makes EVERY assigned task
 * render as unassigned — left the whole suite green, because the only test
 * covering it sliced the source file and the definition sat 590 lines outside
 * the slice.
 *
 * `unassignedValue` is passed in rather than imported so this module stays free
 * of UI constants.
 */
export function assigneeSelectValue(task, unassignedValue) {
  return task?.assigneeId == null ? unassignedValue : String(task.assigneeId);
}

/**
 * Whether reordering must be disabled.
 *
 * Same story: this was `filtersActive(filters)` inline, and the test greped the
 * source for the string that used it. Setting the flag to a constant `false`
 * removed the drag lock, the column greying and the "3 of 47" count while the
 * suite stayed green. A drop inside a filtered column renumbers cards the user
 * cannot see, so this is the one rule on the branch that must be pinned by
 * behaviour rather than by grep.
 */
export function dragLocked(filters) {
  return filtersActive(filters);
}
